/**
 * Relative time in words — "5 hours ago", "7 days ago".
 *
 * The long form of {@link formatTimeAgo}, for places with room to read a sentence rather than a
 * right-aligned column of `3h`/`2d` stamps.
 */
export function formatTimeAgoLong(dateStr: string): string {
  if (!dateStr) return '';
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60_000);

  if (mins < 1) return 'just now';
  if (mins < 60) return plural(mins, 'minute');

  const hours = Math.floor(mins / 60);
  if (hours < 24) return plural(hours, 'hour');

  // Days run to a fortnight before rolling up: "7 days ago" is a sharper answer than "1 week
  // ago" at the age where someone is still deciding whether a session is worth reopening.
  const days = Math.floor(hours / 24);
  if (days < 14) return plural(days, 'day');
  if (days < 45) return plural(Math.floor(days / 7), 'week');

  const months = Math.floor(days / 30);
  if (months < 12) return plural(months, 'month');
  return plural(Math.floor(days / 365), 'year');
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? '' : 's'} ago`;
}

export function formatTimeAgo(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 7)}w ago`;
}
